/**
 * Custom Production Stepper - Vertical Timeline
 * Scroll reveal animation using IntersectionObserver
 */
(() => {
  'use strict';

  const ROOT = '[data-production-timeline]';
  const ITEM = '[data-timeline-item]';

  function init(root) {
    if (!root || root.dataset.timelineInitialized === 'true') return;
    root.dataset.timelineInitialized = 'true';

    const items = Array.from(root.querySelectorAll(ITEM));
    if (!items.length) return;

    const updateTrackState = () => {
      const desktopTrack = root.querySelector('.c-production-timeline__track--desktop');
      if (desktopTrack && desktopTrack.querySelectorAll) {
        desktopTrack.querySelectorAll('.c-production-timeline__line-segment').forEach((seg) => {
          const fromIndex = parseInt(seg.dataset.fromIndex, 10);
          const fromItem = items.find(el => parseInt(el.dataset.index, 10) === fromIndex);
          seg.classList.toggle('is-active', fromItem && fromItem.classList.contains('is-active'));
        });
      }
    };
    
    let updateAllSegmentsRef = null;

    const setupDesktopLineSegments = () => {
      const desktopTrack = root.querySelector('.c-production-timeline__track--desktop');
      if (!desktopTrack || desktopTrack.querySelector('.c-production-timeline__line-segment')) return;

      const desktopItems = Array.from(desktopTrack.querySelectorAll(ITEM))
        .sort((a, b) => (parseInt(a.dataset.index, 10) || 0) - (parseInt(b.dataset.index, 10) || 0));
      if (desktopItems.length < 2) return;

      const container = document.createElement('div');
      container.className = 'c-production-timeline__axis-segments';
      desktopTrack.insertBefore(container, desktopTrack.firstChild);

      const updateAllSegments = () => {
        const containerRect = container.getBoundingClientRect();
        desktopTrack.querySelectorAll('.c-production-timeline__line-segment').forEach((seg) => {
          const fromIndex = parseInt(seg.dataset.fromIndex, 10);
          const fromItem = desktopItems[fromIndex];
          const toItem = desktopItems[fromIndex + 1];
          if (!fromItem || !toItem) return;
          const fromDot = fromItem.querySelector('.c-production-stepper__dot-circle');
          const toDot = toItem.querySelector('.c-production-stepper__dot-circle');
          if (!fromDot || !toDot) return;
          const fromRect = fromDot.getBoundingClientRect();
          const toRect = toDot.getBoundingClientRect();
          const fromCenterY = fromRect.top + fromRect.height / 2 - containerRect.top;
          const toCenterY = toRect.top + toRect.height / 2 - containerRect.top;
          seg.style.top = fromCenterY + 'px';
          seg.style.height = (toCenterY - fromCenterY) + 'px';
        });
      };

      updateAllSegmentsRef = updateAllSegments;

      for (let i = 0; i < desktopItems.length - 1; i++) {
        const seg = document.createElement('div');
        seg.className = 'c-production-timeline__line-segment';
        seg.dataset.fromIndex = desktopItems[i].dataset.index || i;
        container.appendChild(seg);
      }

      updateAllSegments();
      window.addEventListener('resize', updateAllSegments);
      window.addEventListener('load', updateAllSegments);
      requestAnimationFrame(updateAllSegments);
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => requestAnimationFrame(updateAllSegments));
        ro.observe(desktopTrack);
      }

      /* Re-run after layout settles (images, fonts, etc.) */
      [300, 600, 1000].forEach((ms) => setTimeout(() => requestAnimationFrame(updateAllSegments), ms));

      /* Re-run when images in timeline load */
      desktopTrack.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', () => requestAnimationFrame(updateAllSegments));
      });
    };
    
    const setActiveByIndex = (activeIndex) => {
      if (activeIndex < 0) return;
      
      // Activate all items (both tracks) with index <= activeIndex
      items.forEach((el) => {
        const idx = parseInt(el.dataset.index, 10);
        if (idx <= activeIndex) el.classList.add('is-active');
      });
      
      updateTrackState();
    };

    // Setup desktop line segments (only on desktop, after layout)
    const runSetup = () => {
      if (window.matchMedia('(min-width: 1000px)').matches) {
        setTimeout(setupDesktopLineSegments, 200);
      }
    };
    runSetup();
    window.matchMedia('(min-width: 1000px)').addEventListener('change', (e) => {
      if (e.matches) runSetup();
    });

    // Fallback for browsers without IntersectionObserver
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-revealed'));
      setActiveByIndex(0);
      updateTrackState();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            const itemIndex = parseInt(entry.target.dataset.index, 10);
            if (!isNaN(itemIndex)) {
              setActiveByIndex(itemIndex);
            }
            /* Recalculate line segments when item reveals (layout may change) */
            if (updateAllSegmentsRef && window.matchMedia('(min-width: 1000px)').matches) {
              requestAnimationFrame(updateAllSegmentsRef);
            }
          }
        });

        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];

        if (visible) {
          const idx = parseInt(visible.target.dataset.index, 10);
          if (!isNaN(idx)) setActiveByIndex(idx);
        }
      },
      {
        threshold: [0, 0.25, 0.5, 0.75, 1],
        rootMargin: '-42% 0px -42% 0px',
      }
    );

    items.forEach((el) => io.observe(el));
  }

  function initAll(scope = document) {
    scope.querySelectorAll(ROOT).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
